import { withRetry } from "../../common/utils/withRetry";
import getLogger, { LogGroup, sessionId } from "../../js/core/logger";
import { auth } from "../../js/firebase/auth";
import {
    BadRequestError,
    ConflictError,
    ErrorResponseBody,
    ForbiddenError,
    InternalServerError,
    NotFoundError,
    PaymentRequiredError,
    TooManyRequestsError,
    UnauthorizedError,
    UnprocessableContentError
} from "./types/errors";

const logger = getLogger(LogGroup.API);

declare global {
    interface Window {
        overrideIdToken: string;
        apis: Record<string, string>;
    }
}

async function callHandler<Response, Request>(apiName: string, handlerName: string, request: Request): Promise<Response> {
    let idToken: string | undefined = undefined;
    if (window.overrideIdToken) {
        idToken = window.overrideIdToken;
    } else {
        const currentUser = auth().currentUser;
        if (currentUser) {
            idToken = await currentUser.getIdToken();
        }
    }

    const baseUrl = window.apis[`api-${apiName.toLowerCase()}`];

    // eslint-disable-next-line no-undef
    const headers: HeadersInit = {
        "Content-Type": "application/json",
        "X-Client-Session-Id": sessionId
    };

    if (idToken) {
        headers["Authorization"] = `Bearer ${idToken}`;
    }

    const url = `/${handlerName}`;
    const method = "POST";

    // send api request with retries on 503 (service unavailable)
    const res = await withRetry(
        () => fetch(`${baseUrl}${url}`, {
            method: "POST",
            body: JSON.stringify(request),
            credentials: "same-origin",
            headers
        }),
        {
            checkRetryCondition: (error, response) => {
                if (error) {
                    return error.message.toLowerCase().includes("failed to fetch");
                } else if (response) {
                    return response.status === 503;
                }
            },
            onBeforeRetry: async (error, response, retriesRemaining: number) => {
                const logData = {
                    url,
                    method,
                    status: error ? undefined : response.status,
                    body: request,
                    endpointName: handlerName,
                    apiName,
                    requestId: error ? undefined : response.headers.get("X-Request-Id"),
                    retriesRemaining
                };

                // We do it here because we don't want to throw an error, and log the request details
                //  we want to redirect to the maintenance page instead
                if (response?.status === 423) {
                    return;
                }

                logger.warn(`[callable][${apiName}] request failed, retrying`, logData);
            }
        }
    );

    if (res.status < 400) {
        return res.json() as Promise<Response>;
    }

    const requestId = res.headers.get("X-Request-Id");

    const responseBody: ErrorResponseBody = (await res.json().catch(() => { })) ?? {};

    let error: Error;
    if (res.status === 400) {
        error = new BadRequestError(responseBody.message, responseBody);
    } else if (res.status === 404) {
        error = new NotFoundError(responseBody.message, responseBody);
    } else if (res.status === 401) {
        error = new UnauthorizedError(responseBody.message, responseBody);
    } else if (res.status === 403) {
        error = new ForbiddenError(responseBody.message, responseBody);
    } else if (res.status === 429) {
        error = new TooManyRequestsError(responseBody.message, responseBody);
    } else if (res.status === 409) {
        error = new ConflictError(responseBody.message, responseBody);
    } else if (res.status === 402) {
        error = new PaymentRequiredError(responseBody.message, responseBody);
    } else if (res.status === 422) {
        error = new UnprocessableContentError(responseBody.message, responseBody);
    } else if (res.status === 423 && idToken) {
        // We do it here because we don't want to throw an error, and log the request details
        //  we want to redirect to the maintenance page instead
        window.location.href = "/maintenance";
        return;
    } else {
        error = new InternalServerError(responseBody.message, responseBody);
    }

    const logData = {
        url,
        method: method,
        status: res.status,
        body: request,
        requestId,
        endpointName: handlerName,
        apiName
    };

    logger.error(error, `[callable][${apiName}] request failed`, logData);

    throw error;
}

type OmitAuth<T> = T extends (request: infer R, auth: any) => Promise<infer Response>
    ? (request: R) => Promise<Response>
    : never;

type Callables<Handlers> = {
    [K in keyof Handlers]: OmitAuth<Handlers[K]>
};

export function createCallable<Handlers>(apiName: string) {
    return new Proxy({}, {
        get(target, handlerName: string) {
            return (request: any) => callHandler(apiName, handlerName, request);
        }
    }) as unknown as Callables<Handlers>;
}
